import type { ContractSeasonData, UercCalculationSeasonData } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import type { seasonItems } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { SeasonItemsIdsEnum } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { Button, IconEdit } from 'uikit';

import cn from 'classnames';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import type { UseFormClearErrors } from 'react-hook-form/dist/types/form';

import { ConfirmationDialog } from '../ConfirmationDialog';

import { OtherManualSeasonDistributionModal } from './OtherManualSeasonDistributionModal';

import Styles from './otherManualSeasonDistribution.module.css';

export const OtherManualSeasonDistribution = ({
    isDisabled,
    constructionTypeId,
    constructionTypeName,
    costRounding,
    informerMessage,
    tooltipMessage,
    control,
    clearErrors,
    seasons,
}: {
    isDisabled: boolean;
    constructionTypeId: number;
    constructionTypeName: string;
    costRounding: number;
    informerMessage?: string;
    tooltipMessage?: string;
    control: Control<any>;
    seasons: (ContractSeasonData | UercCalculationSeasonData)[];
    clearErrors?: UseFormClearErrors<any>;
}) => {
    const [isOpened, setIsOpened] = useState(false);
    const [defaultDistValue, setDefaultDistValue] = useState<any>();

    const { setValue, getValues } = useFormContext();

    const { cost, method, manualDistribution } = useWatch<{
        cost: (string | undefined)[];
        method: (typeof seasonItems)[0][];
        manualDistribution: { [key: `season_${number}`]: number | string }[];
    }>({
        control,
        defaultValue: {
            manualDistribution: [],
        },
    });

    useEffect(() => {
        if (!defaultDistValue) {
            setDefaultDistValue(structuredClone(manualDistribution?.[constructionTypeId] ?? {}));
        }
    }, [constructionTypeId, defaultDistValue, manualDistribution]);

    const currentCost = cost?.[constructionTypeId];
    const currentMethod = method?.[constructionTypeId];

    const isDisabledByValues = useMemo(
        () => !currentCost || currentMethod?.id !== SeasonItemsIdsEnum.MANUAL,
        [currentMethod?.id, currentCost],
    );

    const handlerConfirmReset = useCallback(() => {
        const { manualDistribution: actualState } = getValues();

        actualState[constructionTypeId] = defaultDistValue;
        setValue?.('manualDistribution', actualState);
        setIsOpened(false);
    }, [constructionTypeId, defaultDistValue, getValues, setValue]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: handlerConfirmReset,
        });

    const toggleModalHandler = useCallback(
        (isSubmit?: boolean) => {
            if (
                !isSubmit &&
                !!setValue &&
                R.not(R.equals(manualDistribution?.[constructionTypeId], defaultDistValue))
            ) {
                handleOpenConfirmationDialog();
            } else {
                setDefaultDistValue(getValues()?.manualDistribution?.constructionTypeId);
                clearErrors?.(`cost.${constructionTypeId}`);
                clearErrors?.(`method.${constructionTypeId}`);
                setIsOpened(false);
            }
        },
        [
            clearErrors,
            constructionTypeId,
            defaultDistValue,
            getValues,
            handleOpenConfirmationDialog,
            manualDistribution,
            setValue,
        ],
    );

    const openModalHandler = useCallback(() => {
        setIsOpened(true);
    }, []);

    return (
        <>
            <Button
                className={cn(Styles.manualSeasonDistributionBtn, {
                    [Styles.disabledByType]: isDisabled,
                })}
                view="ghost"
                type="button"
                iconLeft={IconEdit}
                size="s"
                disabled={isDisabled || isDisabledByValues}
                onClick={openModalHandler}
            />
            {isOpened && (
                <OtherManualSeasonDistributionModal
                    isOpened
                    seasons={seasons}
                    constructionTypeName={constructionTypeName}
                    constructionTypeId={constructionTypeId}
                    toggleModalHandler={toggleModalHandler}
                    costRounding={costRounding}
                    currentCost={currentCost}
                    control={control}
                    isDisabled={isDisabledByValues}
                    informerMessage={informerMessage}
                    tooltipMessage={tooltipMessage}
                />
            )}
            <ConfirmationDialog
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить "
            />
        </>
    );
};

OtherManualSeasonDistribution.displayName = 'OtherManualSeasonDistribution';
